import I18n from 'i18n-js';
import { gql } from '@reverbdotcom/commons/src/gql';

import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import * as elog from '@reverbdotcom/commons/src/elog';
import { core_apimessages_Favorite, UpsertMyFavoriteMutation, UpsertMyFavoriteMutationVariables } from '@reverbdotcom/commons/src/gql/graphql';

interface UseAutoSaveFavoriteOptions {
  componentName: string;
  onSuccess?: (savedFavorite: core_apimessages_Favorite, trackingName?: string) => void;
  onError?: (error: Error) => void;
}

export function useAutoSaveFavorite(options: UseAutoSaveFavoriteOptions) {
  const [upsertFavorite] = useMutation<UpsertMyFavoriteMutation, UpsertMyFavoriteMutationVariables>(
    FIND_FAVORITE_PROVIDER_UPSERT_FAVORITE_MUTATION,
  );

  const autoSave = async (queryParams: string, trackingName?: string) => {
    try {
      const result = await upsertFavorite({
        variables: {
          input: {
            queryParams,
            feedEnabled: true,
            emailEnabled: true,
          },
        },
        refetchQueries: ['Core_FindFavorite'],
        awaitRefetchQueries: true,
      });

      const savedFavorite = result.data?.upsertMyFavorite?.favorite;

      if (result.errors?.length) {
        const error = result.errors[0];

        if (error.extensions && error.extensions.code === 'CLIENT_ERROR') {
          const clientErrors = error.extensions.errors;
          options.onError?.(clientErrors[0]);
        } else {
          options.onError?.(Error(I18n.t('commons.errorDisplay.message')));
        }
      } else if (savedFavorite) {
        options.onSuccess?.(savedFavorite, trackingName);
      }

      return result;
    } catch (error) {
      elog.error(
        error.message,
        { componentName: options.componentName, action: 'autoSaveFavorite' },
        error,
      );

      options.onError?.(Error(I18n.t('commons.errorDisplay.message')));

      throw error;
    }
  };

  return { autoSave };
}

export const FIND_FAVORITE_PROVIDER_UPSERT_FAVORITE_MUTATION = gql(`
  mutation Core_FindFavoriteProvider_UpsertMyFavorite($input: Input_core_apimessages_UpsertMyFavoriteRequest) {
    upsertMyFavorite(input: $input) {
      favorite {
        id
        favorited
        emailEnabled
        feedEnabled
        searchableId
        searchableType
        queryParams
        title
        link {
          href
        }
      }
    }
  }
`);
